"use client";

import { Button } from "@/components/ui/Button";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type GuidedTourStep = {
  target: string;
  title: string;
  text: string;
  href?: string;
};

type TargetRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type GuidedTourProps = {
  label: string;
  steps: GuidedTourStep[];
  onStart?: () => void;
  onNavigate?: (href: string) => void;
  autoStart?: boolean;
  placement?: "inline" | "fixed";
  className?: string;
};

const padding = 10;
function getTargetRect(selector: string): TargetRect | null {
  const element = document.querySelector(selector);

  if (!element) return null;

  const rect = element.getBoundingClientRect();

  return {
    top: Math.max(12, rect.top - padding),
    left: Math.max(12, rect.left - padding),
    width: Math.min(window.innerWidth - 24, rect.width + padding * 2),
    height: Math.min(window.innerHeight - 24, rect.height + padding * 2),
  };
}

function getPopoverPosition(rect: TargetRect | null) {
  const width = Math.min(360, window.innerWidth - 32);

  if (!rect) {
    return {
      width,
      top: Math.max(24, window.innerHeight / 2 - 120),
      left: Math.max(16, window.innerWidth / 2 - width / 2),
    };
  }

  const belowTop = rect.top + rect.height + 14;
  const aboveTop = rect.top - 220;
  const top =
    belowTop + 220 < window.innerHeight
      ? belowTop
      : aboveTop > 16
        ? aboveTop
        : 16;
  const left = Math.min(
    Math.max(16, rect.left + rect.width / 2 - width / 2),
    window.innerWidth - width - 16,
  );

  return { width, top, left };
}

export function GuidedTour({
  label,
  steps,
  onStart,
  onNavigate,
  autoStart = false,
  placement = "inline",
  className,
}: GuidedTourProps) {
  const { language } = useLanguage();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const didAutoStartRef = useRef(false);
  const activeStep = steps[activeIndex];
  const isLastStep = activeIndex >= steps.length - 1;
  const copy = {
    cs: {
      start: label,
      close: "Zavřít průvodce",
      previous: "Zpět",
      next: "Další",
      finish: "Hotovo",
      step: "{current} / {total}",
    },
    en: {
      start: label,
      close: "Close guide",
      previous: "Back",
      next: "Next",
      finish: "Done",
      step: "{current} / {total}",
    },
  }[language];

  const updateTarget = useCallback(() => {
    if (!activeStep) {
      setTargetRect(null);
      return;
    }

    setTargetRect(getTargetRect(activeStep.target));
  }, [activeStep]);

  useEffect(() => {
    if (!autoStart) {
      didAutoStartRef.current = false;
      return;
    }
    if (didAutoStartRef.current) return;

    didAutoStartRef.current = true;
    setActiveIndex(0);
    setIsOpen(true);
  }, [autoStart]);

  useEffect(() => {
    if (!isOpen || !activeStep) return;

    document.querySelector(activeStep.target)?.scrollIntoView({
      block: "center",
      inline: "nearest",
      behavior: "smooth",
    });

    const firstFrame = window.requestAnimationFrame(updateTarget);
    const secondFrame = window.setTimeout(updateTarget, 260);
    window.addEventListener("resize", updateTarget);
    window.addEventListener("scroll", updateTarget, true);

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.clearTimeout(secondFrame);
      window.removeEventListener("resize", updateTarget);
      window.removeEventListener("scroll", updateTarget, true);
    };
  }, [activeStep, isOpen, updateTarget]);

  const popover = useMemo(
    () => (isOpen ? getPopoverPosition(targetRect) : null),
    [isOpen, targetRect],
  );

  const closeTour = () => {
    setIsOpen(false);
    setActiveIndex(0);
  };

  const goNext = () => {
    if (isLastStep) {
      const href = activeStep?.href;
      closeTour();
      if (href) {
        onNavigate?.(href);
        router.push(href);
      }
      return;
    }

    setActiveIndex((current) => current + 1);
  };

  const goPrevious = () => {
    setActiveIndex((current) => Math.max(0, current - 1));
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="compact"
        onClick={() => {
          onStart?.();
          setActiveIndex(0);
          setIsOpen(true);
        }}
        className={cn(
          placement === "fixed" && "fixed bottom-5 right-5 z-40 shadow-lg",
          placement === "inline" && "shrink-0",
          className,
        )}
      >
        <QuestionMarkCircleIcon className="size-4" />
        {copy.start}
      </Button>

      {isOpen && activeStep && popover ? (
        <div className="fixed inset-0 z-[80]">
          <div className="absolute inset-0 bg-softiq-primary/35 backdrop-blur-[1px]" />

          {targetRect ? (
            <div
              className="pointer-events-none absolute rounded-2xl border-2 border-softiq-secondary bg-white/10 shadow-[0_0_0_9999px_rgba(11,23,23,0.38)] ring-4 ring-softiq-secondary/20"
              style={{
                top: targetRect.top,
                left: targetRect.left,
                width: targetRect.width,
                height: targetRect.height,
              }}
            />
          ) : null}

          <div
            role="dialog"
            aria-modal="true"
            aria-label={activeStep.title}
            className="guided-tour-panel"
            style={{
              top: popover.top,
              left: popover.left,
              width: popover.width,
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-softiq-primary/45 dark:text-white/45">
                  {copy.step
                    .replace("{current}", String(activeIndex + 1))
                    .replace("{total}", String(steps.length))}
                </p>
                <h2 className="mt-2 text-base font-medium">
                  {activeStep.title}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeTour}
                aria-label={copy.close}
                className="flex size-8 shrink-0 items-center justify-center rounded-full text-softiq-primary/45 transition hover:bg-softiq-secondary-shade-100 hover:text-softiq-primary dark:text-white/45 dark:hover:bg-white/10 dark:hover:text-white"
              >
                <XMarkIcon className="size-4" />
              </button>
            </div>

            <p className="mt-3 text-sm leading-6 text-softiq-primary/65 dark:text-white/65">
              {activeStep.text}
            </p>

            <div className="mt-5 flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="outline"
                size="compact"
                disabled={activeIndex === 0}
                onClick={goPrevious}
              >
                <ArrowLeftIcon className="size-4" />
                {copy.previous}
              </Button>
              <Button type="button" size="compact" onClick={goNext}>
                {isLastStep && !activeStep.href ? copy.finish : copy.next}
                {isLastStep && !activeStep.href ? null : (
                  <ArrowRightIcon className="size-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
